// A catalog item as a tree: the item, its recipes, each recipe's route steps and BOM lines,
// and the operation or material each one points to, with the verified state on every node.
// It opens collapsed past the first level. The same tree, resolved, shows one job item's
// chain with its values, minutes and dollars.

import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { LibCatalogItem, Library, Resolution, ResolvedItem } from '@zerogap/engine';
import { hours, money, qty } from '../lib/format';
import { useSession } from '../lib/session';
import { Empty, Tag } from '../ui/kit';
import { bomPackWords, priceWords } from '../ui/pack';

function Node({ title, right, verified, open, children }: { title: ReactNode; right?: ReactNode; verified?: boolean | null; open?: boolean; children?: ReactNode }) {
  const head = (
    <>
      <span>
        {title} {verified === false && <Tag kind="un">unverified</Tag>}
        {verified === true && <Tag kind="on">verified</Tag>}
      </span>
      {right !== undefined && <span className="lk">{right}</span>}
    </>
  );
  if (!children) return <div className="tree-leaf">{head}</div>;
  return (
    <details open={open}>
      <summary>{head}</summary>
      <div className="tree-kids">{children}</div>
    </details>
  );
}

const minutes = (m: string | number | null | undefined) => `${qty(Number(m ?? 0).toFixed(1))} min`;

/** The library's own tree for one catalog item. */
export function CatalogTree({ item, lib, depth = 0 }: { item: LibCatalogItem; lib: Library; depth?: number }) {
  return (
    <div className="tree">
      <Node title={<b>{item.name}</b>} right={`v${item.version}${item.recipe_standard ? ` · recipe picked by the job's ${item.recipe_standard.replace(/_/g, ' ')}` : ''}`} verified={item.verified} open={depth === 0}>
        {item.recipes.length === 0 && <Empty>No recipe yet.</Empty>}
        {item.recipes.map((r) => (
          <Node key={r.id} title={<>Recipe · {r.name}{r.is_default ? ' (default)' : ''}</>} right={`${r.route.length} steps · ${r.bom.length} lines${r.parts.length ? ` · ${r.parts.length} parts` : ''}`} verified={r.verified}>
            <Node title={`Route · ${r.route.length} steps`}>
              {r.route.length === 0 && <Empty>No route steps.</Empty>}
              {r.route.map((s) => {
                const op = lib.operations[s.operation_id];
                return (
                  <Node key={s.id} title={op?.name ?? 'An operation that is no longer in the library'} right={<code>{s.qty_expr}</code>} verified={op ? op.verified : null}>
                    <div className="tree-leaf">
                      <span>
                        {op?.minutes_per_unit ? `${qty(op.minutes_per_unit)} min ${op.unit ?? 'per unit'}` : 'No time standard yet'}
                        {op?.setup_minutes ? ` · ${qty(op.setup_minutes)} min setup` : ''}
                        {op?.labor_class_id ? ` · ${lib.laborClasses[op.labor_class_id]?.name ?? ''}` : ' · no labor class'}
                      </span>
                    </div>
                    {s.note && (
                      <div className="tree-leaf">
                        <small>{s.note}</small>
                      </div>
                    )}
                  </Node>
                );
              })}
            </Node>
            <Node title={`BOM · ${r.bom.length} lines`}>
              {r.bom.length === 0 && <Empty>No materials.</Empty>}
              {r.bom.map((b) => {
                const m = b.material_id ? lib.materials[b.material_id] : undefined;
                return (
                  <Node key={b.id} title={m ? m.description : b.material_role ? `The job's ${b.material_role.replace(/_/g, ' ')}` : 'No material chosen'} right={<code>{b.qty_expr}</code>} verified={m ? m.verified : null}>
                    <div className="tree-leaf">
                      <span>
                        {m ? `${priceWords(m)}${m.vendor ? ` · ${m.vendor}` : ''}` : b.material_role ? 'Filled per job from the job standard of this name' : ''}
                      </span>
                    </div>
                    {b.note && (
                      <div className="tree-leaf">
                        <small>{b.note}</small>
                      </div>
                    )}
                  </Node>
                );
              })}
            </Node>
            {r.parts.length > 0 && (
              <Node title={`Parts it is cut from · ${r.parts.length}`}>
                {r.parts.map((p) => {
                  const m = p.material_id ? lib.materials[p.material_id] : undefined;
                  return (
                    <Node key={p.id} title={p.name} right={<code>× {p.qty_expr}</code>} verified={m ? m.verified : null}>
                      <div className="tree-leaf">
                        <span>
                          <code>{p.length_expr}</code> by <code>{p.width_expr}</code> · {m ? m.description : p.material_role ? `the job's ${p.material_role.replace(/_/g, ' ')}` : 'no material named'}
                        </span>
                      </div>
                      {p.note && (
                        <div className="tree-leaf">
                          <small>{p.note}</small>
                        </div>
                      )}
                    </Node>
                  );
                })}
              </Node>
            )}
          </Node>
        ))}
        {item.children.length > 0 && (
          <Node title={`Creates on its own · ${item.children.length}`}>
            {item.children.map((c) => {
              const kid = c.child_catalog_item_id ? lib.catalog[c.child_catalog_item_id] : undefined;
              return (
                <Node key={c.id} title={c.label ?? kid?.name ?? 'Child'} right={<code>× {c.count_expr}</code>} verified={kid ? kid.verified : null}>
                  {kid ? (
                    depth < 2 ? (
                      <CatalogTree item={kid} lib={lib} depth={depth + 1} />
                    ) : (
                      <div className="tree-leaf">
                        <Link to={`/library/catalog/${kid.id}`}>{kid.name}</Link>
                      </div>
                    )
                  ) : (
                    <div className="tree-leaf">
                      <span>
                        Whichever item the job names: <code>{c.child_expr}</code>
                      </span>
                    </div>
                  )}
                </Node>
              );
            })}
          </Node>
        )}
      </Node>
    </div>
  );
}

/** The same tree for one item on a job, resolved: the chain with its values, minutes and dollars. */
export function ResolvedTree({ item, res, lib }: { item: ResolvedItem; res: Resolution; lib: Library }) {
  const { can } = useSession();
  const priced = can('see_prices');
  const group = res.groups.find((g) => g.key === item.group_key);
  const cat = item.catalog_item_id ? lib.catalog[item.catalog_item_id] : undefined;
  const recipe = cat?.recipes.find((r) => r.id === item.recipe_id);
  if (!group) return <Empty>This item has no route or materials.</Empty>;
  // the same lines the Item tab lists: the steps and materials this item actually takes a share of
  const steps = group.steps.filter((s) => Number(s.shares[item.id]?.minutes ?? 0) > 0);
  const bom = group.bom.filter((b) => (b.shares[item.id]?.cents ?? 0) > 0 || (Number(b.qty) > 0 && group.item_ids.length === 1));
  const shared = group.item_ids.length > 1;
  const dollars = (cents: number | undefined) => (priced ? money(cents ?? 0) : '—');
  return (
    <div className="tree">
      <Node title={<b>{item.number} · {item.name}</b>} right={<>{dollars(item.price_cents)} · {hours(item.labor_minutes)}</>} verified={cat ? cat.verified : null} open>
        <Node title={<>Recipe · {recipe?.name ?? group.recipe_name ?? 'its own lines'}</>} verified={recipe ? recipe.verified : null} open>
          <Node title={`Route · ${steps.length} steps`} right={<>{hours(item.labor_minutes)} · {dollars(item.labor_cents)}</>}>
            {steps.length === 0 && <Empty>No route steps apply yet.</Empty>}
            {steps.map((s) => (
              <Node key={s.key} title={s.operation_name} right={<>{minutes(s.shares[item.id]?.minutes)} · {dollars(s.shares[item.id]?.cents)}</>} verified={s.verified}>
                <div className="tree-leaf">
                  <span>
                    <code>{s.qty_expr}</code> = {qty(s.qty)} {s.unit ?? ''}
                  </span>
                </div>
                {s.trace.map((t, i) => (
                  <div key={i} className="tree-leaf">
                    <small>
                      {t.name} = {t.value}
                    </small>
                  </div>
                ))}
                <div className="tree-leaf">
                  <span>
                    {qty(s.qty)} × {qty(s.minutes_per_unit)} min + {qty(s.setup_minutes)} setup = {minutes(s.minutes)}
                    {shared ? ' for the group' : ''}
                  </span>
                </div>
                <div className="tree-leaf">
                  <span>
                    at ${s.rate}/h ({s.labor_class_id ? lib.laborClasses[s.labor_class_id]?.name : 'no labor class'}) = {dollars(s.cents)}
                    {shared ? `; this item's share ${dollars(s.shares[item.id]?.cents)}` : ''}
                  </span>
                </div>
                {s.warnings.map((w) => (
                  <div key={w} className="warn">
                    {w}
                  </div>
                ))}
              </Node>
            ))}
          </Node>
          {(item.parts ?? []).length > 0 && (
            <Node title={`Parts · ${item.parts!.length} lines`}>
              {item.parts!.map((p) => (
                <div key={p.part_id} className="tree-leaf">
                  <span>
                    {qty(p.qty)} × {p.name} · {qty(p.length)} by {qty(p.width)} · {p.description}
                  </span>
                </div>
              ))}
            </Node>
          )}
          <Node title={`BOM · ${bom.length} lines`} right={dollars(item.material_cents)}>
            {bom.length === 0 && <Empty>No materials apply yet.</Empty>}
            {bom.map((b) => (
              <Node key={b.key} title={b.description} right={dollars(b.shares[item.id]?.cents ?? b.cents)} verified={b.material_id ? b.verified : null}>
                <div className="tree-leaf">
                  <span>
                    <code>{b.qty_expr}</code> = {qty(b.qty)} {b.unit ?? ''}
                  </span>
                </div>
                {b.trace.map((t, i) => (
                  <div key={i} className="tree-leaf">
                    <small>
                      {t.name} = {t.value}
                    </small>
                  </div>
                ))}
                <div className="tree-leaf">
                  <span>
                    {qty(b.qty)} × ${b.price} = {dollars(b.cents)}
                    {b.pack ? ` (${bomPackWords(b.pack)})` : ''}
                    {shared ? ` for the group; this item's share ${dollars(b.shares[item.id]?.cents)}` : ''}
                    {b.material_role ? ` · filled by the job standard “${b.material_role}”` : ''}
                    {b.vendor ? ` · ${b.vendor}` : ''}
                  </span>
                </div>
                {b.warnings.map((w) => (
                  <div key={w} className="warn">
                    {w}
                  </div>
                ))}
              </Node>
            ))}
          </Node>
        </Node>
      </Node>
    </div>
  );
}
